import { Injectable } from '@angular/core';
import { Timestamp } from 'firebase/firestore';
import { AngularFirestore, CollectionReference } from '@angular/fire/compat/firestore';
import { Logger } from '../logger.service';
import { getDay } from 'date-fns';
import { HttpService } from './http.service';
import { environment } from '@env/environment';
import { formatDate } from '@angular/common';
import { CredentialsService } from '@app/auth';

const log = new Logger('date-service');

interface DateFirebase {
  date: Timestamp;
}

@Injectable({
  providedIn: 'root',
})
export class DateService {
  timeOffset = 0;
  goToThePast = false;
  isLocalHour = true;
  id = 'now';
  now?: Date;
  path = 'dates';
  private collection: CollectionReference<DateFirebase>;
  private timestampErrorOccurred = false; // Flag para evitar múltiples llamadas si hay error

  constructor(
    private firestore: AngularFirestore,
    private httpS: HttpService,
    private credentialsService: CredentialsService
  ) {
    this.collection = this.firestore.collection<DateFirebase>(this.path).ref;
    if (this.goToThePast) {
      this.hourChange(this.id);
    } else {
      this.now = new Date();
    }
    this.now = new Date();
  }

  private async getMexicoCityCloudDate(): Promise<Date> {
    // Si ya hubo un error, no hacer más llamadas
    if (this.timestampErrorOccurred) {
      return new Date();
    }

    // Solo hacer la llamada si hay sesión iniciada
    if (!this.credentialsService.isAuthenticated()) {
      return new Date();
    }

    try {
      const dataDate = (await this.httpS.post(environment.api.timestamp.url, {})) as any;

      const { now } = dataDate as { now: string };
      const [date, time] = now.split(', ');
      let [day, month, year] = date.split('/');
      let [hours, minutes, seconds] = time.split(':');

      if (+day < 10) day = `0${day}`;
      if (+month < 10) month = `0${month}`;

      if (+hours < 10) hours = `0${hours}`;

      const newDate = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds)
      );

      // Si la llamada fue exitosa, resetear el flag de error
      this.timestampErrorOccurred = false;
      return newDate;
    } catch (error) {
      log.error(error);
      // Marcar que hubo un error para evitar más llamadas
      this.timestampErrorOccurred = true;
    }

    return new Date();
  }

  async dateNow(): Promise<Date> {
    if (this.now && this.goToThePast) {
      const now = await this.getMexicoCityCloudDate();
      if (this.isLocalHour) {
        this.now.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
        return new Date(this.now);
      } else {
        return new Date(this.now);
      }
    }
    if (this.goToThePast) {
      const dataDate = await this.getById(this.id);
      if (dataDate) {
        // // Fecha original (puede ser cualquier fecha)
        // const originalDate = new Date(dataDate.date.toDate());

        // // Zona horaria de la Ciudad de México
        // const mexicoCityTimeZone = 'America/Mexico_City';

        // // Convertir la fecha original a la zona horaria de la Ciudad de México
        // const mexicoCityDate = toZonedTime(originalDate, mexicoCityTimeZone);

        // // Formatear la fecha para que sea legible
        // const formattedDate = format(mexicoCityDate, 'yyyy-MM-dd HH:mm:ss', {
        //   timeZone: mexicoCityTimeZone,
        // });

        // log.info(formattedDate);

        const d = dataDate.date.toDate();
        if (this.isLocalHour) {
          const now = await this.getMexicoCityCloudDate();
          d.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
        }

        return d;
      }
    }

    return await this.getMexicoCityCloudDate();
  }

  async timestampNow(): Promise<Timestamp> {
    if (this.now) {
      return Timestamp.fromDate(this.now);
    }

    if (this.goToThePast) {
      const dataDate = await this.getById(this.id);
      if (dataDate) {
        return dataDate.date;
      }
    }
    return Timestamp.now();
  }

  private async getById(id: string): Promise<DateFirebase | null> {
    const snap = await this.collection.doc(id).get();
    if (snap.exists) {
      const data = { ...snap.data() } as DateFirebase;
      return data;
    } else {
      return null;
    }
  }

  private hourChange(id: string): void {
    this.firestore
      .collection<DateFirebase>(this.path)
      .doc(id)
      .valueChanges()
      .subscribe((dt) => {
        if (dt) {
          this.now = dt.date.toDate();
          if (this.isLocalHour) {
            const now = new Date();
            this.now.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
          }
        }
      });
  }

  dateToMexicoCity(dateString: string | Date) {
    // Supongamos que tienes una fecha UTC
    const utcDate = new Date(dateString); // Ejemplo de fecha UTC '2023-10-05T12:00:00Z'

    const formattedMexicoTime = formatDate(utcDate, 'yyyy-MM-dd HH:mm:ss', 'es-MX');

    log.info(formattedMexicoTime);

    return formattedMexicoTime;
  }

  /**
   * Obtiene el número del día de la semana (0 para domingo, 1 para lunes, ..., 6 para sábado)
   *  */
  getWeekdayInMexicoCity(date: Date): number {
    // Obtiene la fecha y hora actual en UTC
    const now = new Date(date);

    // Obtiene el número del día de la semana (0 para domingo, 1 para lunes, ..., 6 para sábado)
    const weekdayNumber = getDay(now);
    return weekdayNumber;
  }

  /**
   * Obtiene la hora en formato de la ciudad de mexico
   *  */
  getHoursInMexicoCity(date: Date): number {
    // Zona horaria de la Ciudad de México
    const mexicoCityTimeZone = 'America/Mexico_City';

    // Obtiene la fecha y hora actual en UTC
    const now = new Date(date);

    const formattedTime = formatDate(now, 'HH', 'es-MX');

    return +formattedTime;
  }

  dateFromMexicoCity(dateString: string) {
    const mexicoCityDate = new Date(dateString); // Ejemplo de fecha en la zona horaria de la Ciudad de México

    const formattedUtcTime = formatDate(mexicoCityDate, 'yyyy-MM-dd HH:mm:ss', 'es-MX');

    log.info(formattedUtcTime);

    return new Date(formattedUtcTime);
  }

  getSimulatedDate(apiDate: string | null): Date {
    const now = new Date();

    const dateStr = apiDate;

    const [day, month, year] = dateStr!.split('/');
    // Nota: Meses en JavaScript son 0-based (0=enero, 6=julio)
    const baseDate = new Date(
      parseInt(year),
      parseInt(month) - 1, // Restamos 1 al mes
      parseInt(day)
    );

    baseDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

    return baseDate;
  }

  async verifyCloseDayIGT() {
    // Obtenemos la hora actual en UTC (hora universal)
    const ahoraUTC = await this.dateNow();
    const horaActualFormateada = formatDate(ahoraUTC, 'HH:mm', 'es-MX');

    // Definimos los límites del rango horario en formato HH:mm
    const horaInicio = '21:00'; // Hora de inicio del rango
    const horaFin = '22:40'; // Hora de fin del rango

    // Convertimos las horas formateadas a números para poder compararlas
    const horaActualNumerica = parseInt(horaActualFormateada.replace(':', ''), 10);
    const horaInicioNumerica = parseInt(horaInicio.replace(':', ''), 10);
    const horaFinNumerica = parseInt(horaFin.replace(':', ''), 10);

    // Verificamos si la hora actual está dentro del rango
    return horaActualNumerica >= horaInicioNumerica && horaActualNumerica <= horaFinNumerica;
  }

  async getPreciseNow(): Promise<Date> {
    try {
      // Obtener fecha del servidor y calcular offset
      const serverTime = await this.getMexicoCityCloudDate();
      const localTime = new Date();
      this.timeOffset = serverTime.getTime() - localTime.getTime();

      return new Date(localTime.getTime() + this.timeOffset);
    } catch (error) {
      console.error('Error obteniendo fecha del servidor, usando fecha local', error);
      return new Date();
    }
  }

  /**
   * Parsea fechas de Lotenal como hora local (Ciudad de México).
   * - "2026-09-16" → medianoche local del día de celebración
   * - "2026-09-16 18:00:00" → fecha/hora local de cierre
   */
  parseLotenalDateString(dateStr?: string): Date | undefined {
    if (!dateStr) {
      return undefined;
    }

    const trimmed = dateStr.trim();
    const lotenalMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?$/);

    if (lotenalMatch) {
      const [, year, month, day, hours = '0', minutes = '0', seconds = '0'] = lotenalMatch;
      const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        Number(seconds)
      );

      return isNaN(date.getTime()) ? undefined : date;
    }

    const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
    const date = new Date(normalized);
    return isNaN(date.getTime()) ? undefined : date;
  }
}
